import type { ReactNode } from 'react';
import React, { useRef } from 'react';
import { useExpandableContent } from '@noths/polaris-client-react-hooks';

import { ExpandableContentToggleButton } from 'src/components/atoms/ExpandableContentToggleButton';
import { expandableContentWrapper } from 'src/styles/useExpandableContent';
import type { Filter } from 'src/types';

const MIN_HEIGHT = 240;

export interface ExpandableContentTogglePayload {
  isExpanded: boolean;
  filterTitle: string;
}

interface FilterOptionsExpandableContentProps {
  filter: Filter;
  isExpandable: boolean;
  onExpandableContentToggle: (payload: ExpandableContentTogglePayload) => void;
  children: ReactNode;
}

export const FilterOptionsExpandableContent = ({
  filter,
  isExpandable,
  onExpandableContentToggle,
  children,
}: FilterOptionsExpandableContentProps) => {
  const elementWrapper = useRef<HTMLDivElement | null>(null);
  const elementWrapperId = `expandable_filter__element_wrapper__${filter.uid}`;

  const { isExpanded, height, toggleExpanded } = useExpandableContent({
    minHeight: MIN_HEIGHT,
    isExpandable,
    elementWrapper,
    observeSubtree: true,
  });

  const handleToggleExpanded = () => {
    toggleExpanded();
    onExpandableContentToggle({ isExpanded, filterTitle: filter.title });
  };

  return (
    <div>
      <div
        ref={elementWrapper}
        css={isExpandable ? expandableContentWrapper : undefined}
        style={{ height }}
        aria-expanded={isExpanded}
        id={elementWrapperId}
      >
        {children}
      </div>
      {isExpandable && (
        <ExpandableContentToggleButton
          toggleExpanded={handleToggleExpanded}
          isExpanded={isExpanded}
          elementId={elementWrapperId}
        />
      )}
    </div>
  );
};
